import type { GeneratedNpc, GeneratedMission, GeneratedLocation } from '@bid/shared-types';

export type ContentType = 'npc' | 'mission' | 'location';
export type SavedContent = {
  id: number;
  type: ContentType;
  name: string;
  content: GeneratedNpc | GeneratedMission | GeneratedLocation;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
};

const STORAGE_KEY = 'bid-content-library';

function getStorage(): SavedContent[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    const parsed = JSON.parse(data);
    return parsed.map((item: SavedContent) => ({
      ...item,
      createdAt: new Date(item.createdAt),
      updatedAt: new Date(item.updatedAt),
    }));
  } catch {
    return [];
  }
}

function setStorage(data: SavedContent[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export async function saveContent(
  type: ContentType,
  name: string,
  content: GeneratedNpc | GeneratedMission | GeneratedLocation,
  tagNames: string[] = []
): Promise<number> {
  const items = getStorage();
  const id = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;

  const newItem: SavedContent = {
    id,
    type,
    name,
    content,
    tags: tagNames,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  items.push(newItem);
  setStorage(items);
  return id;
}

export async function getContent(id: number): Promise<SavedContent | null> {
  const items = getStorage();
  return items.find(item => item.id === id) || null;
}

export async function getAllContent(type?: ContentType): Promise<SavedContent[]> {
  const items = getStorage();
  if (type) {
    return items.filter(item => item.type === type);
  }
  return items;
}

export async function searchContent(query: string): Promise<SavedContent[]> {
  const items = getStorage();
  const lowerQuery = query.toLowerCase();
  return items.filter(
    item =>
      item.name.toLowerCase().includes(lowerQuery) ||
      JSON.stringify(item.content).toLowerCase().includes(lowerQuery)
  );
}

export async function deleteContent(id: number): Promise<void> {
  const items = getStorage();
  const filtered = items.filter(item => item.id !== id);
  setStorage(filtered);
}

export async function updateContent(
  id: number,
  updates: Partial<Pick<SavedContent, 'name' | 'tags'>>
): Promise<void> {
  const items = getStorage();
  const index = items.findIndex(item => item.id === id);
  if (index !== -1) {
    items[index] = {
      ...items[index],
      ...updates,
      updatedAt: new Date(),
    };
    setStorage(items);
  }
}
